import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/adminApi';
import './RerunDialog.css';

export type SnapshotMode = 'latest' | 'original';

export interface RerunRequest {
    pipelineConfig: string;
    snapshotMode: SnapshotMode;
}

interface RerunDialogProps {
    onClose: () => void;
    onRun: (request: RerunRequest) => void;
}

export function RerunDialog({ onClose, onRun }: RerunDialogProps) {
    const [selectedPipelineConfig, setSelectedPipelineConfig] = useState<string>('');
    const [snapshotMode, setSnapshotMode] = useState<SnapshotMode>('latest');

    const { data: pipelineConfigsData } = useQuery({
        queryKey: ['pipeline-configs'],
        queryFn: adminApi.listPipelineConfigs,
    });

    // The system-wide default (AppSettings.defaultPipelineConfig) — the config a
    // student's session would run against absent an override/experiment.
    const { data: settings } = useQuery({
        queryKey: ['settings'],
        queryFn: adminApi.getSettings,
    });

    const pipelineOptions = useMemo(() => (
        (pipelineConfigsData?.configs || []).map(c => ({ label: c.name, value: c.name }))
    ), [pipelineConfigsData]);

    // Preselect the configured default once both queries resolve, unless the
    // operator has already picked something.
    useEffect(() => {
        if (selectedPipelineConfig) return;
        const def = settings?.defaultPipelineConfig;
        if (def && pipelineOptions.some(o => o.value === def)) {
            setSelectedPipelineConfig(def);
        }
    }, [settings, pipelineOptions, selectedPipelineConfig]);

    return (
        <div className="modal-overlay">
            <div className="modal-content rerun-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Rerun Submission</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="rerun-section-label">Question &amp; student state</div>
                    <div className="rerun-snapshot-mode">
                        <label className={`rerun-snapshot-option ${snapshotMode === 'latest' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="rerun-snapshot-mode"
                                value="latest"
                                checked={snapshotMode === 'latest'}
                                onChange={() => setSnapshotMode('latest')}
                            />
                            <span>
                                <span className="rerun-snapshot-name">Latest</span>
                                <span className="rerun-snapshot-hint">Use current question + student's profile.</span>
                            </span>
                        </label>
                        <label className={`rerun-snapshot-option ${snapshotMode === 'original' ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="rerun-snapshot-mode"
                                value="original"
                                checked={snapshotMode === 'original'}
                                onChange={() => setSnapshotMode('original')}
                            />
                            <span>
                                <span className="rerun-snapshot-name">Original snapshot</span>
                                <span className="rerun-snapshot-hint">Reuse question + student profile from the original ModelInput.</span>
                            </span>
                        </label>
                    </div>

                    <div className="rerun-section-label">Pipeline</div>
                    {pipelineOptions.length === 0 ? (
                        <p style={{ color: 'var(--text-tertiary)' }}>No analysis pipelines configured.</p>
                    ) : (
                        <select
                            className="rerun-pipeline-select"
                            value={selectedPipelineConfig}
                            onChange={e => setSelectedPipelineConfig(e.target.value)}
                        >
                            <option value="">Select a pipeline…</option>
                            {pipelineOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.value === settings?.defaultPipelineConfig ? `${opt.label} (default)` : opt.label}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
                <div className="modal-footer">
                    <div className="footer-spacer" />
                    <button className="btn-cancel" onClick={onClose}>Cancel</button>
                    <button
                        className="btn-save"
                        onClick={() => onRun({ pipelineConfig: selectedPipelineConfig, snapshotMode })}
                        disabled={!selectedPipelineConfig}
                    >
                        Run
                    </button>
                </div>
            </div>
        </div>
    );
}
